<script setup lang="ts">
  import { computed } from 'vue'
  import { X } from 'lucide-vue-next'
  import { Input } from '@/components/ui/input'
  import { Switch } from '@/components/ui/switch'
  import FontInput from './FontInput.vue'
  import type { TextStyle } from '@/types'

  const props = defineProps<{
    label: string
    style: TextStyle
    detectedFonts?: string[]
  }>()

  const emit = defineEmits<{ update: [value: TextStyle] }>()

  const swatchStyle = computed(() => {
    if (props.style.color) {
      return { backgroundColor: props.style.color }
    }
    return {
      backgroundImage: 'repeating-linear-gradient(45deg, #d1d5db 0, #d1d5db 1.5px, white 0, white 50%)',
      backgroundSize: '5px 5px',
    }
  })

  function setColor(value: string | null) {
    emit('update', { ...props.style, color: value })
  }
</script>

<template>
  <div class="flex items-center gap-4 py-1.5 group">
    <!-- Field label -->
    <span class="w-24 shrink-0 text-xs text-muted-foreground capitalize">{{ label }}</span>

    <!-- Color swatch -->
    <div class="relative w-10 shrink-0 flex justify-center">
      <label
        class="w-5 h-5 rounded-full cursor-pointer block ring-1 ring-border transition-transform hover:scale-110"
        :style="swatchStyle"
        :title="style.color ?? 'Default (inherited)'">
        <input
          type="color"
          class="sr-only"
          :value="style.color ?? '#000000'"
          @input="(e) => setColor((e.target as HTMLInputElement).value)" />
      </label>
      <button
        v-if="style.color"
        class="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-muted border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        title="Reset to default"
        @click.prevent="setColor(null)">
        <X class="w-2 h-2" />
      </button>
    </div>

    <!-- Font — fixed width, readable but not overly wide -->
    <FontInput
      class="w-44 shrink-0"
      :model-value="style.font ?? null"
      :detected-fonts="detectedFonts"
      @update:model-value="(v) => emit('update', { ...style, font: v })" />

    <!-- Size -->
    <Input
      type="number"
      min="6"
      max="120"
      class="w-14 h-7 text-xs shrink-0"
      placeholder="—"
      :model-value="style.size ?? ''"
      @update:model-value="(v) => emit('update', { ...style, size: v ? Number(v) : null })" />

    <!-- Bold -->
    <div class="w-10 shrink-0 flex justify-center">
      <Switch
        :model-value="style.bold ?? false"
        @update:model-value="(v) => emit('update', { ...style, bold: v })" />
    </div>

    <!-- Optional extra column (e.g. position) -->
    <slot />
  </div>
</template>
